import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Mail, FileSearch, FileText, ExternalLink } from 'lucide-react';
import ledgerApi from '../lib/ledgerApi';
import { fmtMonth } from '../utils/dateFormat';

/**
 * "Needs Attention" widget — surfaces actionable items across all
 * the user's linked businesses. Pending invites + unmatched bank
 * lines + monthly newsletter status (KNG only for now).
 */
export default function NeedsAttention({ user, businesses, getPendingInvites }) {
  const [pendingInvites, setPendingInvites] = useState(0);
  const [bankPending, setBankPending] = useState({}); // { entityId: count }
  const [loading, setLoading] = useState(true);

  const ownedBusinesses = useMemo(
    () => businesses.filter((b) => b.roles?.[user.uid] === 'owner'),
    [businesses, user.uid]
  );
  const linkedBusinesses = useMemo(
    () => ownedBusinesses.filter((b) => b.ledgerEntityId),
    [ownedBusinesses]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);

      // Pending invites across all owned businesses
      let totalInvites = 0;
      for (const b of ownedBusinesses) {
        try {
          const invites = await getPendingInvites(b.id);
          totalInvites += invites.length;
        } catch { /* swallow */ }
      }
      if (!cancelled) setPendingInvites(totalInvites);

      // Unmatched bank items per linked entity
      const bankMap = {};
      await Promise.all(
        linkedBusinesses.map(async (b) => {
          try {
            const summary = await ledgerApi.bankSummary(b.ledgerEntityId);
            const total = summary.reduce((s, a) => s + (a.pending_count || 0), 0);
            if (total > 0) bankMap[b.ledgerEntityId] = { name: b.name, count: total };
          } catch { /* entity may not exist in ledger */ }
        })
      );
      if (!cancelled) setBankPending(bankMap);

      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [ownedBusinesses, linkedBusinesses, getPendingInvites]);

  // Newsletter: previous month YYYY-MM
  const prevMonth = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const totalBankPending = Object.values(bankPending).reduce((s, x) => s + x.count, 0);
  const hasKng = linkedBusinesses.some((b) => b.ledgerEntityId === 'kng');
  const hasItems = pendingInvites > 0 || totalBankPending > 0 || hasKng;

  if (loading) return null;
  if (!hasItems) return null;

  return (
    <div className="needs-attention">
      <div className="needs-attention-header">
        <AlertCircle size={14} /> <span>Needs Attention</span>
      </div>
      <div className="needs-attention-grid">
        {pendingInvites > 0 && (
          <Link to="/access" className="na-card">
            <div className="na-card-icon"><Mail size={18} /></div>
            <div className="na-card-body">
              <div className="na-card-count">{pendingInvites}</div>
              <div className="na-card-label">Pending {pendingInvites === 1 ? 'invite' : 'invites'}</div>
            </div>
            <ExternalLink size={12} className="na-card-chev" />
          </Link>
        )}

        {totalBankPending > 0 && Object.entries(bankPending).map(([entityId, info]) => (
          <a
            key={entityId}
            href={ledgerApi.deepLink(entityId, 'bank')}
            target="_blank" rel="noreferrer"
            className="na-card"
          >
            <div className="na-card-icon"><FileSearch size={18} /></div>
            <div className="na-card-body">
              <div className="na-card-count">{info.count}</div>
              <div className="na-card-label">{info.name}: unmatched bank</div>
            </div>
            <ExternalLink size={12} className="na-card-chev" />
          </a>
        ))}

        {hasKng && (
          <a
            href={ledgerApi.deepLink('kng', 'newsletter')}
            target="_blank" rel="noreferrer"
            className="na-card na-card-info"
          >
            <div className="na-card-icon"><FileText size={18} /></div>
            <div className="na-card-body">
              <div className="na-card-count">{fmtMonth(prevMonth)}</div>
              <div className="na-card-label">Generate KNG newsletter</div>
            </div>
            <ExternalLink size={12} className="na-card-chev" />
          </a>
        )}
      </div>
    </div>
  );
}
